import { extractProductPageForItem } from "./api/ProcurementClient";
import Rfq from "./models/Rfq";
import {
  ExtractedProductPageResult,
  ProductPageSnapshot,
} from "./models/ProductPageExtraction";

/** Fired on `window` after a pull from the shopping extension is queued for a line item. */
export const SHOPPING_PAGE_PULL_READY_EVENT = "shoppingPagePullReady";

export interface ShoppingPagePullReadyDetail {
  itemId: number;
}

interface PendingPagePull {
  page: ProductPageSnapshot;
  /** Already read for the pop-up preview; skips a second extraction. */
  extracted?: ExtractedProductPageResult;
}

/** Keyed by line item; the line's panel may mount after the pull arrives. */
const pendingByItemId = new Map<number, PendingPagePull>();

export function takePendingShoppingPagePull(
  itemId: number
): PendingPagePull | undefined {
  const pending = pendingByItemId.get(itemId);
  pendingByItemId.delete(itemId);
  return pending;
}

/** Extension dispatches `shoppingPagePull` in the app tab when the buyer clicks "Pull into line". */
export function registerShoppingPagePullListener(): () => void {
  const handler = (event: Event) => {
    const detail = (
      event as CustomEvent<{
        itemId?: number;
        page?: ProductPageSnapshot;
        extracted?: ExtractedProductPageResult;
      }>
    ).detail;
    const itemId = Number(detail?.itemId);
    const url = detail?.page?.url?.trim();
    if (!Number.isFinite(itemId) || itemId <= 0 || !url || !detail?.page) {
      return;
    }
    pendingByItemId.set(itemId, {
      page: { ...detail.page, url },
      ...(detail.extracted?.product ? { extracted: detail.extracted } : {}),
    });
    window.dispatchEvent(
      new CustomEvent<ShoppingPagePullReadyDetail>(SHOPPING_PAGE_PULL_READY_EVENT, {
        detail: { itemId },
      })
    );
  };
  window.addEventListener("shoppingPagePull", handler);
  return () => window.removeEventListener("shoppingPagePull", handler);
}

type PreviewProductPage = (detail: {
  itemId: number;
  page: ProductPageSnapshot;
}) => Promise<ExtractedProductPageResult>;

export interface ShoppingLineInfo {
  lineNumber: number | null;
  name: string;
  partNumbers: string[];
}

type GetShoppingLineInfo = (itemId: number) => ShoppingLineInfo | null;

declare global {
  interface Window {
    __procurementPreviewProductPage?: PreviewProductPage;
    __procurementShoppingLineInfo?: GetShoppingLineInfo;
  }
}

/**
 * The extension calls this in the app tab (MAIN world) to read a vendor page for the
 * pull pop-up's preview, using the app's signed-in API session.
 */
export function registerShoppingPagePreviewBridge(
  getRfqs: () => Rfq[]
): () => void {
  const preview: PreviewProductPage = async ({ itemId, page }) => {
    const rfq = getRfqs().find((row) =>
      (row.quotes ?? []).some((hw) =>
        (hw.items ?? []).some((line) => line.id === itemId)
      )
    );
    if (!rfq) {
      throw new Error("Open this RFQ in the procurement app, then try again.");
    }
    return extractProductPageForItem(rfq.id, itemId, page);
  };
  const lineInfo: GetShoppingLineInfo = (itemId) => {
    for (const rfq of getRfqs()) {
      for (const hw of rfq.quotes ?? []) {
        const items = hw.items ?? [];
        const index = items.findIndex((line) => line.id === itemId);
        if (index < 0) continue;
        const line = items[index];
        return {
          lineNumber: index + 1,
          name: line.name ?? "",
          partNumbers: (line.itemNumbers ?? [])
            .map((n) => n.value?.trim() ?? "")
            .filter(Boolean),
        };
      }
    }
    return null;
  };
  window.__procurementPreviewProductPage = preview;
  window.__procurementShoppingLineInfo = lineInfo;
  return () => {
    if (window.__procurementPreviewProductPage === preview) {
      delete window.__procurementPreviewProductPage;
    }
    if (window.__procurementShoppingLineInfo === lineInfo) {
      delete window.__procurementShoppingLineInfo;
    }
  };
}
